import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldCheck, ChevronRight } from "lucide-react";

/**
 * HomePopup — one-time attention-grabbing popup shown on the Home page.
 * Fades in dramatically, auto-dismisses after 10 seconds, remembers dismissal
 * for 3 days via localStorage so returning visitors aren't nagged.
 */
const STORAGE_KEY = "ss_home_popup_dismissed_at";
const NAG_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;    // 3 days
const AUTO_DISMISS_MS = 10000;                       // 10 seconds

export default function HomePopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (Date.now() - last < NAG_INTERVAL_MS) return;   // recently dismissed
    } catch (_) { /* SSR / private mode — show anyway */ }
    // Delay entrance so it feels dramatic after the hero paints
    const openT = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(openT);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => close(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) { /* noop */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none"
      aria-live="polite"
      data-testid="home-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss popup"
        className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm pointer-events-auto popup-fadein"
        data-testid="home-popup-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-lg pointer-events-auto popup-pop"
        role="dialog"
        aria-labelledby="home-popup-title"
      >
        <div className="rounded-2xl bg-[#0A0A0A] shadow-2xl border-2 border-brand overflow-hidden">
          {/* Accent header */}
          <div className="h-2 bg-gradient-to-r from-brand via-gold-500 to-brand" aria-hidden="true" />

          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="inline-flex items-center gap-2 bg-brand-light text-brand border border-brand/30 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-outfit font-bold">
                <ShieldCheck className="w-3.5 h-3.5" /> Why SecuSource
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="w-8 h-8 rounded-full hover:bg-[#141414] flex items-center justify-center text-white/60"
                data-testid="home-popup-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h2 id="home-popup-title" className="text-2xl sm:text-3xl font-outfit font-bold text-white leading-tight">
              Stop managing security deployments through WhatsApp, spreadsheets and phone calls.
            </h2>

            <p className="text-white/70 mt-4 font-manrope leading-relaxed">
              SecuSource brings trusted professionals, proven teams and operational tools together in one secure platform.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/register/company"
                onClick={close}
                className="inline-flex items-center gap-1 bg-brand hover:bg-brand-hover text-white font-manrope font-semibold px-4 py-2.5 rounded-md text-sm"
                data-testid="home-popup-cta-company"
              >
                Register as a Company <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to="/register/staff"
                onClick={close}
                className="inline-flex items-center gap-1 border border-white/15 hover:bg-[#0E0E0E] text-white font-manrope font-semibold px-4 py-2.5 rounded-md text-sm"
                data-testid="home-popup-cta-pro"
              >
                Join as a Professional
              </Link>
            </div>

            {/* Timer bar — auto-dismiss visual */}
            <div className="mt-6 h-1 rounded-full bg-[#141414] overflow-hidden" aria-hidden="true">
              <div className="h-full bg-brand popup-timer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
